import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { testIds } from "@/testIds";
import { readLatexSource } from "@/lib/latexSource";
import { latexLanguage } from "@/lib/codemirror/latex-language";
import { SourceCodeViewer } from "@/components/editor/preview/SourceCodeViewer";
import { rhythmText, shownLineNumbers } from "@/lib/codemirror/latex-rhythm";
import { blockSlices, parsePreamble, splitPreamble, type BlockSlice } from "@/components/editor/preview/latexView";
import i18n from "i18next";
import { LatexTabBar, useLatexTab, type LatexTab } from "@/components/editor/preview/LatexTabBar";
import { LatexBlocksTab, typeLabel } from "@/components/editor/preview/LatexBlocksTab";
import { LatexPreambleTab } from "@/components/editor/preview/LatexPreambleTab";
import type { ReaderBlock } from "@/lib/mobileReader";
import tabStyles from "@/components/editor/preview/LatexViewTabs.module.scss";
import { cn } from "@/lib/cn";
import styles from "./MobilePreview.module.scss";

const M = testIds.mobile;
const L = testIds.latexView;

type Fetch = (url: string, init?: RequestInit) => Promise<Response>;

export interface MobileLatexViewProps {
  fetchWithAuth: Fetch;
  documentId: string;
  /** Changes to this read the source again (a change Lilia made). */
  refreshKey?: number;
  /** A block whose lines are highlighted and scrolled to. */
  highlightBlockId?: string | null;
  /** The document's blocks, for the Blocks tab's rendered cards. */
  blocks?: readonly ReaderBlock[];
  /** The tab, when the reader's tab row picks it (PDF | Preamble · Code · Blocks). Without it the
   * view has its own tab bar. */
  tab?: LatexTab;
  onTabChange?: (tab: LatexTab) => void;
}

const NO_BLOCKS: readonly ReaderBlock[] = [];

/**
 * View LaTeX on a phone (Olivia, 10 Oct): the source the PDF is built from,
 * read-only, monospace, with line numbers, scrolling both ways, and Copy. No
 * download. A selected block's lines are highlighted. The source is drawn by
 * the desktop's own viewer (CodeMirror, read-only), so it has the same LaTeX
 * colours (the user, 10 Oct).
 *
 * Since 18 Oct it has the desktop's Preamble · Code · Blocks tabs (DESIGN-14), read-only, as
 * Ḥassān drew them for phones: denser cards, folded preamble groups, and a card's lines opening
 * Code under a "From … · Back to block" bar. Code wraps long lines (his phone round, 18 Oct).
 */
export function MobileLatexView({ fetchWithAuth, documentId, refreshKey = 0, highlightBlockId = null, blocks = NO_BLOCKS, tab: tabProp, onTabChange }: MobileLatexViewProps) {
  const { t } = useTranslation("mobile");
  const [ownTab, setOwnTab] = useLatexTab();
  const tab = tabProp ?? ownTab;
  const setTab = onTabChange ?? setOwnTab;
  // A card's "lines a–b" opens Code at those lines, under a bar naming the card, until Code is left
  // or another block is selected. "Back to block" returns to that card.
  const [fromCard, setFromCard] = useState<{ slice: BlockSlice; label: string } | null>(null);
  const [revealBlockId, setRevealBlockId] = useState<string | null>(null);
  useEffect(() => setFromCard(null), [highlightBlockId]);
  useEffect(() => { if (tab !== "code") setFromCard(null); }, [tab]);
  const [raw, setRaw] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const ctrl = new AbortController();
    setFailed(false);
    (async () => {
      try {
        const res = await fetchWithAuth(`/api/documents/${documentId}/preview/latex`, { signal: ctrl.signal });
        if (!res.ok) throw new Error(String(res.status));
        const body = (await res.json()) as { content?: string };
        if (!ctrl.signal.aborted) setRaw(body.content ?? "");
      } catch {
        if (!ctrl.signal.aborted) setFailed(true);
      }
    })();
    return () => ctrl.abort();
  }, [fetchWithAuth, documentId, refreshKey]);

  const source = useMemo(() => (raw === null ? null : readLatexSource(raw)), [raw]);
  const range = fromCard?.slice.range ?? (highlightBlockId && source ? source.ranges.get(highlightBlockId) ?? null : null);
  const blockStarts = useMemo(() => (source ? [...source.ranges.values()].map((r) => r.start) : []), [source]);
  const titleIds = useMemo(() => new Set(blocks.filter((b) => b.type === "title").map((b) => b.id)), [blocks]);
  const slices = useMemo(() => (source ? blockSlices(source.lines.join("\n"), source.ranges, titleIds) : []), [source, titleIds]);
  // Cards name their lines as Code numbers them: the viewer doesn't count the blank lines between blocks.
  const shown = useMemo(() => (source ? shownLineNumbers(source.lines.join("\n"), blockStarts) : (n: number) => n), [source, blockStarts]);
  const shownRange = useCallback((r: { start: number; end: number }) => ({ start: shown(r.start), end: shown(r.end) }), [shown]);
  const cardSlices = useMemo(() => slices.map((sl) => ({ ...sl, range: shownRange(sl.range) })), [slices, shownRange]);
  const preamble = useMemo(() => (source ? parsePreamble(splitPreamble(source.lines.join("\n")).preamble) : null), [source]);
  const cardBlocks = useMemo(() => [...blocks], [blocks]);

  const copy = async () => {
    if (!source) return;
    try {
      // What is shown: one blank line between blocks (Olivia, 11 Oct).
      await navigator.clipboard.writeText(rhythmText(source.lines.join("\n"), blockStarts));
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // The clipboard refused; the source stays selectable.
    }
  };

  if (failed) return <p className={styles.message}>{t("latex.failed")}</p>;
  if (!source) return <p className={styles.message}>{t("latex.loading")}</p>;

  const tabBar = tabProp ? null : <LatexTabBar tab={tab} onChange={setTab} className={tabStyles.phoneTabs} />;

  if (tab !== "code") {
    return (
      <div className={cn(styles.latex, tabStyles.phone)} data-testid={M.latexView} data-latex-tab={tab}>
        {tabBar}
        {tab === "blocks" ? (
          <LatexBlocksTab
            documentId={documentId}
            slices={cardSlices}
            blocks={cardBlocks}
            focusedBlockId={highlightBlockId}
            variant="phone"
            revealBlockId={revealBlockId}
            onOpenLines={(_r, card) => {
              const slice = slices.find((sl) => sl.blockId === card.blockId) ?? card;
              const type = blocks.find((b) => b.id === slice.blockId)?.type ?? "block";
              setFromCard({ slice, label: typeLabel(type) });
              setRevealBlockId(null);
              setTab("code");
            }}
          />
        ) : (
          preamble && <LatexPreambleTab summary={preamble} variant="phone" />
        )}
      </div>
    );
  }

  const from = fromCard;
  return (
    <div className={cn(styles.latex, tabStyles.phone)} data-testid={M.latexView} data-latex-tab="code">
      {tabBar}
      {from ? (
        <div className={tabStyles.codeContext} data-testid={L.codeContext}>
          <span>
            {i18n.t("editor:latexView.blocks.from")}{" "}
            <b>
              {from.label} ·{" "}
              {shown(from.slice.range.start) === shown(from.slice.range.end)
                ? i18n.t("editor:latexView.blocks.line", { n: shown(from.slice.range.start) })
                : i18n.t("editor:latexView.blocks.lines", { start: shown(from.slice.range.start), end: shown(from.slice.range.end) })}
            </b>
          </span>
          <button
            type="button"
            data-testid={L.backToBlock}
            onClick={() => { setRevealBlockId(from.slice.blockId); setTab("blocks"); }}
          >
            {i18n.t("editor:latexView.blocks.backToBlock")}
          </button>
        </div>
      ) : (
        <div className={styles.latexBar}>
          <span>
            {t("latex.lines", { count: source.lines.length })}{range ? ` · ${t("latex.highlighted", { start: shown(range.start), end: shown(range.end) })}` : ""}
          </span>
          <button type="button" className={styles.fix} data-testid={M.latexCopy} onClick={() => void copy()}>
            {copied ? t("latex.copied") : t("latex.copy")}
          </button>
        </div>
      )}
      <div className={styles.latexScroller}>
        <SourceCodeViewer value={source.lines.join("\n")} language={latexLanguage} highlightRange={range ?? undefined} density="compact" wrap foldPreamble rhythm={{ blockStarts, phone: true }} />
      </div>
    </div>
  );
}
